import * as React from 'react';
import { useCallback, useEffect } from 'react';

import config from 'js/app/config';
import { setLocation } from 'js/lib/coursera.redirect';
import { useRetracked } from 'js/lib/retracked';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Button } from '@coursera/cds-core';
import type { ButtonProps, IconProps } from '@coursera/cds-core';
import { ErrorIcon } from '@coursera/cds-icons';

import useIsLegalConsolidationFlowAndHasTouQueryParam from 'bundles/authentication-common/hooks/useIsInLegalConsolidationFlowAndHasTouQueryParam';
import IdealUpgradeEnrollModal from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IdealUpgradeEnrollModal';
import IndiaPriceTestUpgradeModal from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IndiaUpgrade/IndiaPriceTestUpgradeModal';
import { useCourseraPlusSubscriptionFreeTrials } from 'bundles/coursera-plus-common/hooks/useCourseraPlusSubscriptionFreeTrials';
import { useEnrollInOrUpgradeToCourseraPlus } from 'bundles/coursera-plus-common/hooks/useEnrollInOrUpgradeToCourseraPlus';
import { useGetSubscriptionTiersProductPrices } from 'bundles/coursera-plus-common/hooks/useGetSubscriptionTiersProductPrices';
import { isCourseraPlusCollectionsCheckoutCtaTarget } from 'bundles/coursera-plus-common/utils/courseraPlusCollectionsUtils';
import { productPriceToMonetaryAmount } from 'bundles/coursera-plus-common/utils/productPriceUtils';
import { getIsLearnerImpressedInIndiaTiersEpic } from 'bundles/coursera-plus-common/utils/subscriptionTiersUtils';
import { useExistingCourseraLiteSubscription } from 'bundles/coursera-plus-common/utils/withExistingCourseraLiteSubscription';
import { ENROLL } from 'bundles/enroll-common/utils/enrollActionParams';
import { MONTHLY } from 'bundles/subscriptions-common/common/BillingCycleType';

import _t from 'i18n!nls/coursera-plus-common';

export type ButtonPropsType = ButtonProps['variant'];

type PropsFromCaller = {
  ctaLabel?: string | null;
  ctaTarget?: string | null;
  ctaVariant?: ButtonPropsType;
  trackingName: string;
  trackEnrollCtaClick?: () => void;
  ctaIcon?: React.ReactElement<IconProps>;
  shouldCheckActionParams?: boolean;
  // HeroModule and Information do not have the required data to allow the experiment data flow.
  shouldDisableIndiaExperiment?: boolean;
  // If true, the post-auth checkout is intended to be triggered using `useCourseraPlusPostAuthEnrollFromUrl` hook instead of the button mount
  shouldUseHookForPostAuthCheckout?: boolean;
  ctaSize?: ButtonProps['size'];
};

const EnrollOrUpgradeToCourseraPlus = ({
  ctaLabel,
  ctaVariant,
  trackingName,
  ctaIcon,
  trackEnrollCtaClick,
  shouldCheckActionParams,
  shouldUseHookForPostAuthCheckout,
  shouldDisableIndiaExperiment,
  ctaTarget,
  ctaSize = 'medium',
}: PropsFromCaller) => {
  const { query, pathname, search } = useLocation();
  const navigate = useNavigate();
  const isUserAuthenticated = isAuthenticatedUser();
  const [idealUpgradeModalOpen, setIdealUpgradeModalOpen] = React.useState(false);
  const { existingCourseraLiteSubscription, allSubscriptions } = useExistingCourseraLiteSubscription();
  const isSubscribedToCourseraPlus = allSubscriptions?.some(
    (subscription) =>
      subscription.isCourseraPlusSubscription &&
      (subscription.isActive || subscription.isFreeTrial || subscription.isCancelled)
  );
  const { courseraPlusMonthlyProductPrice } = useGetSubscriptionTiersProductPrices({
    skip: !existingCourseraLiteSubscription?.id,
    shouldGetCourseraPlusAnnual: false,
  });
  const { userHasCourseraPlusFreeTrial } = useCourseraPlusSubscriptionFreeTrials();
  const retrackedTrack = useRetracked();
  const isCourseraPlusCollectionsEnrollment = isCourseraPlusCollectionsCheckoutCtaTarget(ctaTarget);
  const collectionId = isCourseraPlusCollectionsEnrollment ? stringKeyToTuple(ctaTarget ?? '')?.[1] : undefined;
  const isLegalConsolidationFlowAndHasTouQueryParam = useIsLegalConsolidationFlowAndHasTouQueryParam();
  const {
    isSubmitting: isEnrollingInCourseraPlus,
    handleEnrollInCourseraPlus,
    error: cPlusenrollmentError,
  } = useEnrollInOrUpgradeToCourseraPlus(collectionId, shouldUseHookForPostAuthCheckout);

  // After the first Coursera Plus CTA mounts, useEnrollOrUpgradeToCPlusButton flips a ref so
  // shouldCheckActionParams becomes false for all CTAs. We still need to auto-enroll after the user
  // finishes the TOU modal; pendingEnrollAfterTouRef records that case
  const pendingEnrollAfterTouRef = React.useRef(false);

  const enrollOrUpgradeToCourseraPlus = useCallback<() => void>(() => {
    if (existingCourseraLiteSubscription?.id) {
      setIdealUpgradeModalOpen(true);
    } else if (isSubscribedToCourseraPlus && collectionId) {
      const searchParams = new URLSearchParams(search);
      searchParams.set('myLearningTab', 'SAVED');
      searchParams.set('collectionId', collectionId);
      setLocation(`${config.url.base}my-learning?${searchParams.toString()}`);
    } else {
      handleEnrollInCourseraPlus();
    }
  }, [handleEnrollInCourseraPlus, existingCourseraLiteSubscription, isSubscribedToCourseraPlus, collectionId, search]);

  // If the user is authenticated and the query param is 'enroll', then enroll the user into Coursera Plus.
  // This is to automatically trigger the enrollment flow after the user logs in.
  useEffect(() => {
    if (!shouldUseHookForPostAuthCheckout && isUserAuthenticated && query?.action === ENROLL) {
      const isSelectedCtaForActionParams = Boolean(shouldCheckActionParams);

      if (isSelectedCtaForActionParams && isLegalConsolidationFlowAndHasTouQueryParam) {
        pendingEnrollAfterTouRef.current = true;
      }

      const shouldAutoEnroll =
        !isLegalConsolidationFlowAndHasTouQueryParam &&
        (isSelectedCtaForActionParams || pendingEnrollAfterTouRef.current);

      if (shouldAutoEnroll) {
        pendingEnrollAfterTouRef.current = false;

        // Remove the action query param and replace browser history to prevent checkout redirect
        // from happening again if user decides to press the back button from checkout
        const queryParams = new URLSearchParams(search);
        queryParams.delete('action');
        const searchParams = queryParams.toString();
        navigate({ pathname, search: searchParams ? `?${searchParams}` : undefined }, { replace: true });

        enrollOrUpgradeToCourseraPlus();
      }
    }
  }, [
    query,
    pathname,
    search,
    navigate,
    isUserAuthenticated,
    enrollOrUpgradeToCourseraPlus,
    shouldUseHookForPostAuthCheckout,
    shouldCheckActionParams,
    isLegalConsolidationFlowAndHasTouQueryParam,
  ]);

  const onClose = () => {
    setIdealUpgradeModalOpen(false);
  };

  if (isSubscribedToCourseraPlus && !isCourseraPlusCollectionsEnrollment) {
    return null;
  }

  if (!shouldDisableIndiaExperiment && getIsLearnerImpressedInIndiaTiersEpic()) {
    return <IndiaPriceTestUpgradeModal onClose={onClose} />;
  }

  const handleButtonClick = () => {
    trackEnrollCtaClick?.();

    if (trackingName) {
      retrackedTrack({
        trackingName,
        trackingData: {},
        action: 'click',
      });
    }

    enrollOrUpgradeToCourseraPlus();
  };

  const getButtonIcon = () => {
    if (cPlusenrollmentError) {
      return <ErrorIcon color="error" />;
    } else if (ctaIcon) {
      return ctaIcon;
    }
    return undefined;
  };
  // Default CTA label from the caller.
  // Used when the learner is eligible for a Coursera Plus free trial
  let _ctaLabel = ctaLabel;
  if (existingCourseraLiteSubscription) {
    _ctaLabel = _t('Switch to Coursera Plus');
  } else if (isSubscribedToCourseraPlus && isCourseraPlusCollectionsEnrollment) {
    _ctaLabel = _t('Save Collection');
  } else if (!userHasCourseraPlusFreeTrial) {
    _ctaLabel = _t('Join Coursera Plus');
    if (isCourseraPlusCollectionsEnrollment) {
      _ctaLabel = _t('Renew Coursera Plus');
    }
  }

  return (
    <>
      <Button
        component="button"
        variant={ctaVariant || 'primary'}
        loading={isEnrollingInCourseraPlus}
        icon={getButtonIcon()}
        onClick={handleButtonClick}
        size={ctaSize}
      >
        {_ctaLabel}
      </Button>

      {idealUpgradeModalOpen && courseraPlusMonthlyProductPrice && (
        <IdealUpgradeEnrollModal
          courseraPlusProductPrice={productPriceToMonetaryAmount(courseraPlusMonthlyProductPrice)}
          onClose={onClose}
          isStandaloneCourseraPlusUpgrade={true}
          toTierBillingCycle={MONTHLY}
        />
      )}
    </>
  );
};

export default EnrollOrUpgradeToCourseraPlus;
